<template>
  <div class="organize-match-page" :class="config.themeClass">
    <MainNavbar />
    <div class="discover-teams-page" :class="config.themeClass">
      <div v-if="config.previewMessage" class="sport-organize-preview" role="status">
        <i class="fa-solid fa-circle-info" aria-hidden="true" />
        <p>{{ config.previewMessage }}</p>
      </div>

      <!-- Header Section -->
      <div class="page-header">
        <div class="header-content">
          <h1>
            <i :class="config.icon" aria-hidden="true" />
            <span class="header-title-text">{{ config.title }}</span>
          </h1>
          <p>{{ config.subtitle }}</p>
        </div>
      </div>

      <!-- Search and Filters Section -->
      <div class="search-section">
        <div class="search-container">
          <!-- Main Search Bar -->
          <div class="search-bar-container">
            <div class="search-bar">
              <i class="fa-solid fa-magnifying-glass search-icon"></i>
                <input
                v-model="searchQuery"
                  type="text"
                placeholder="Search teams by name..."
                @input="debouncedSearch"
                @focus="onSearchFocus"
                @blur="onSearchBlur"
                class="search-input"
                :class="{ 'focused': searchFocused }"
              >
              <transition name="fade-scale">
                <button 
                  v-if="searchQuery" 
                  @click="clearSearch" 
                  class="clear-search"
                  aria-label="Clear search"
                >
                  <i class="fa-solid fa-times"></i>
                </button>
              </transition>
              </div>
            <button 
              @click="toggleFilters" 
              class="btn-filter"
              :class="{ 'active': showFilters }"
            >
              <i class="fa-solid fa-filter"></i>
              <span>Filters</span>
              <transition name="bounce">
                <span v-if="activeFiltersCount > 0" class="filter-badge">{{ activeFiltersCount }}</span>
              </transition>
            </button>
              </div>

          <!-- Filters Panel -->
          <transition name="slide-down">
            <div v-if="showFilters" class="filters-panel">
              <div class="filters-header">
                <h3><i class="fa-solid fa-sliders"></i> Filter Teams</h3>
                <button @click="clearAllFilters" class="btn-clear-filters">
                  <i class="fa-solid fa-rotate-left"></i>
                  Clear All
                </button>
                </div>

              <div class="filters-grid">
                <!-- County Filter -->
                <div class="filter-group">
                  <label><i class="fa-solid fa-map"></i> County</label>
                  <select v-model="filters.county" @change="onCountyChange">
                    <option value="">All Counties</option>
                    <option 
                      v-for="county in availableCounties" 
                      :key="county"
                      :value="county"
                    >
                      {{ county }}
                    </option>
                  </select>
              </div>

                <!-- Constituency Filter -->
                <div class="filter-group">
                  <label><i class="fa-solid fa-map-location-dot"></i> Constituency</label>
                <select
                    v-model="filters.constituency" 
                    :disabled="!filters.county"
                  >
                    <option value="">{{ filters.county ? 'All Constituencies' : 'Select county first' }}</option>
                    <option 
                      v-for="constituency in availableConstituencies" 
                      :key="constituency"
                      :value="constituency"
                    >
                      {{ constituency }}
                    </option>
                </select>
              </div>

                <!-- Team Type Filter -->
                <div class="filter-group">
                  <label><i class="fa-solid fa-users"></i> Team Type</label>
                  <select v-model="filters.team_type" @change="searchTeams">
                    <option value="">All Types</option>
                    <option value="preschool">Pre-School</option>
                    <option value="highschool">High School</option>
                    <option value="academy">Academy/Youth</option>
                    <option value="casual">Casual/Recreational</option>
                    <option value="competitive">Competitive</option>
                    <option value="corporate">Corporate</option>
                    <option value="community">Community</option>
                  </select>
          </div>

                <!-- Skill Level Filter -->
                <div class="filter-group">
                  <label><i class="fa-solid fa-trophy"></i> Skill Level</label>
                  <select v-model="filters.skill_level" @change="searchTeams">
                    <option value="">All Levels</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="elite">Elite</option>
                  </select>
            </div>

                <!-- Availability Filter -->
                <div class="filter-group">
                  <label><i class="fa-solid fa-calendar-check"></i> Availability</label>
                  <select v-model="filters.availability" @change="searchTeams">
                    <option value="">All</option>
                    <option value="weekends">Weekends only</option>
                    <option value="weekdays">Weekdays only</option>
                    <option value="both">Both weekends and weekdays</option>
                    <option value="flexible">Flexible</option>
                  </select>
              </div>

                <!-- Sport-specific filters -->
                <div
                  v-for="extra in config.extraFilters"
                  :key="extra.key"
                  class="filter-group"
                >
                  <label>
                    <i :class="extra.icon" aria-hidden="true" />
                    {{ extra.label }}
                  </label>
                  <select v-model="filters[extra.key]" @change="onSportFilterChange">
                    <option
                      v-for="opt in extra.options"
                      :key="`${extra.key}-${opt.value}`"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>

                <!-- Member Count Range -->
                <div class="filter-group filter-range">
                  <label><i class="fa-solid fa-user-group"></i> Member Count</label>
                  <div class="range-inputs">
                  <input
                      v-model.number="filters.min_members"
                      type="number"
                      placeholder="Min"
                      min="0"
                      class="range-input"
                      @input="debouncedFilter"
                    >
                    <span class="range-separator">to</span>
                  <input
                      v-model.number="filters.max_members"
                      type="number"
                      placeholder="Max"
                      min="0"
                      class="range-input"
                      @input="debouncedFilter"
                  >
                </div>
              </div>

                <!-- Verified Only Toggle -->
                <div class="filter-group filter-checkbox">
                  <label class="checkbox-label">
                    <input 
                      type="checkbox" 
                      v-model="filters.verified_only"
                      @change="searchTeams"
                    >
                    <span><i class="fa-solid fa-check-circle"></i> Verified teams only</span>
                </label>
              </div>
              </div>
          </div>
          </transition>
            </div>
              </div>

      <!-- Results Section -->
      <div class="results-section">
        <div class="results-header">
          <div class="results-info">
            <h2>
              <transition name="fade" mode="out-in">
                <span v-if="loading" key="loading" class="loading-text">
                  <i class="fa-solid fa-spinner fa-spin"></i> Searching...
                </span>
                <span v-else key="results" class="results-text">
                  Found <strong class="highlight">{{ totalTeams }}</strong> team{{ totalTeams !== 1 ? 's' : '' }}
                </span>
              </transition>
            </h2>
            <transition name="fade">
              <p v-if="!loading && totalTeams > 0" class="results-subtext">
                Showing {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, totalTeams) }} of {{ totalTeams }}
              </p>
            </transition>
              </div>
          <div class="sort-options">
            <label>Sort by:</label>
            <select v-model="sortBy" @change="searchTeams" class="sort-select">
              <option value="relevance">Relevance</option>
              <option value="verified">Verified First</option>
              <option value="activity">Most Active</option>
              <option value="newest">Newest</option>
                </select>
          </div>
              </div>

        <!-- Loading State with Skeleton -->
        <transition name="fade">
          <div v-if="loading" class="loading-state" key="loading">
            <div class="skeleton-grid">
              <div v-for="n in 6" :key="n" class="skeleton-card">
                <div class="skeleton-logo"></div>
                <div class="skeleton-content">
                  <div class="skeleton-line skeleton-title"></div>
                  <div class="skeleton-line skeleton-subtitle"></div>
                  <div class="skeleton-line skeleton-text"></div>
                  <div class="skeleton-line skeleton-text short"></div>
                </div>
              </div>
              </div>
          </div>
        </transition>

        <!-- Empty State -->
        <transition name="fade-scale">
          <div v-if="!loading && teams.length === 0" class="empty-state" key="empty">
            <div class="empty-icon">
              <i :class="config.discoverEnabled ? 'fa-solid fa-users-slash' : config.icon"></i>
            </div>
            <h3>{{ config.discoverEnabled ? "No teams found" : "Search not live yet" }}</h3>
            <p>
              {{
                config.discoverEnabled
                  ? "Try adjusting your filters or search query"
                  : config.previewMessage
              }}
            </p>
            <button
              v-if="config.discoverEnabled"
              type="button"
              class="btn btn-primary btn-empty"
              @click="clearAllFilters"
            >
              <i class="fa-solid fa-filter"></i>
              Clear Filters
            </button>
          </div>
        </transition>

        <!-- Teams Grid -->
        <transition-group v-if="!loading && teams.length > 0" name="team-list" tag="div" class="teams-grid" key="teams">
          <div
            v-for="(team, index) in teams"
            :key="team.team_id"
            class="team-card"
            :style="{ '--delay': index * 0.05 + 's' }"
            @click="selectTeam(team)"
          >
            <div class="team-card-header">
              <div class="team-logo">
                <img
                  v-if="team.logo_url"
                  :src="team.logo_url"
                  :alt="team.name"
                  loading="lazy"
                  decoding="async"
                  @error="handleImageError"
                >
                <div v-else class="logo-placeholder">
                  <i class="fa-solid fa-users"></i>
                  </div>
                <transition name="scale">
                  <span v-if="team.is_verified" class="verified-badge" title="Verified Team">
                    <i class="fa-solid fa-check-circle"></i>
                  </span>
                </transition>
                  </div>
              <div class="team-info">
                <h3>{{ team.name }}</h3>
                <p class="team-type">{{ getTeamTypeLabel(team.team_type) }}</p>
                  </div>
                  </div>

            <div class="team-card-body">
              <p class="team-bio" v-if="team.bio">
                {{ truncateText(team.bio, 100) }}
              </p>
              
              <div class="team-stats">
                <div class="stat-item">
                  <i class="fa-solid fa-users"></i>
                  <span>{{ team.member_count }} members</span>
                  </div>
                <div class="stat-item">
                  <i :class="config.matchStatIcon" aria-hidden="true"></i>
                  <span>{{ team.total_matches }} matches</span>
                  </div>
                <div class="stat-item" v-if="team.total_matches > 0">
                  <i class="fa-solid fa-trophy"></i>
                  <span>{{ team.win_rate }}% win rate</span>
                </div>
              </div>

              <div class="team-location">
                <i class="fa-solid fa-map-marker-alt"></i>
                <span>{{ team.county }}{{ team.constituency ? `, ${team.constituency}` : '' }}</span>
                  </div>

              <div class="team-tags">
                <span v-if="team.game_format" class="tag tag-format">{{ team.game_format }}</span>
                <span v-if="team.court_type" class="tag tag-format">{{ team.court_type }}</span>
                <span v-if="team.pitch_surface" class="tag tag-format">{{ team.pitch_surface }}</span>
                <span v-if="team.match_format" class="tag tag-format">{{ team.match_format }}</span>
                <span class="tag tag-skill">{{ getSkillLevelLabel(team.skill_level) }}</span>
                <span class="tag tag-availability">{{ getAvailabilityLabel(team.availability) }}</span>
                </div>
              </div>

            <div class="team-card-footer">
              <button
                type="button"
                class="btn btn-primary btn-sm btn-challenge-team"
                @click.stop="selectTeam(team)"
              >
                <i class="fa-solid fa-handshake" aria-hidden="true"></i>
                <span class="btn-challenge-team-text">{{ config.challengeButtonLabel }}</span>
              </button>
              </div>
            </div>
        </transition-group>

        <!-- Pagination -->
        <transition name="fade">
          <div v-if="totalPages > 1" class="pagination">
            <button 
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="btn-pagination"
            >
              <i class="fa-solid fa-chevron-left"></i> 
              <span class="pagination-text">Previous</span>
              </button>
            
            <div class="page-numbers">
              <button 
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="['btn-page', { 'active': page === currentPage }]"
              >
                {{ page }}
              </button>
            </div>

            <button 
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="btn-pagination"
            >
              <span class="pagination-text">Next</span>
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </transition>
      </div>

      <!-- Team Selection Modal -->
      <transition name="modal">
        <div v-if="selectedTeam" class="modal-overlay" @click="closeTeamModal">
          <div class="modal-content team-modal" @click.stop>
          <div class="modal-header">
              <h3><i class="fa-solid fa-handshake"></i> Organize Match</h3>
              <button class="btn-close" @click="closeTeamModal" aria-label="Close modal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
              <div class="selected-team-info">
                <div class="team-logo-large">
                  <img
                    v-if="selectedTeam.logo_url"
                    :src="selectedTeam.logo_url"
                    :alt="selectedTeam.name"
                    decoding="async"
                    @error="handleImageError"
                  >
                  <div v-else class="logo-placeholder-large">
                    <i class="fa-solid fa-users"></i>
              </div>
                </div>
                <h4>{{ selectedTeam.name }}</h4>
                <p class="team-details">
                  <span>{{ getTeamTypeLabel(selectedTeam.team_type) }}</span> •
                  <span>{{ getSkillLevelLabel(selectedTeam.skill_level) }}</span> •
                  <span>{{ selectedTeam.member_count }} members</span>
                </p>
              </div>

              <form @submit.prevent="organizeMatch" class="match-form">
                <div class="form-group">
                  <label for="matchDate">
                    <i class="fa-solid fa-calendar"></i> Match Date *
                  </label>
                  <input
                    id="matchDate"
                    v-model="matchForm.match_date"
                    type="date"
                    :min="minDate"
                    :class="{ 'error': errors.match_date }"
                  >
                  <transition name="slide-down">
                    <span v-if="errors.match_date" class="error-message">
                      <i class="fa-solid fa-exclamation-circle"></i>
                      {{ errors.match_date }}
                    </span>
                  </transition>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="matchTime">
                      <i class="fa-solid fa-clock"></i> Match Time *
                    </label>
                    <input
                      id="matchTime"
                      v-model="matchForm.match_time"
                      type="time"
                      :class="{ 'error': errors.match_time }"
                    >
                    <transition name="slide-down">
                      <span v-if="errors.match_time" class="error-message">
                        <i class="fa-solid fa-exclamation-circle"></i>
                        {{ errors.match_time }}
                      </span>
                    </transition>
                </div>

                  <div class="form-group">
                    <label for="duration">
                      <i class="fa-solid fa-hourglass-half"></i> Duration *
                    </label>
                    <select
                      id="duration"
                      v-model="matchForm.duration"
                      :class="{ 'error': errors.duration }"
                    >
                      <option value="">Select duration</option>
                      <option
                        v-for="opt in config.durationOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <transition name="slide-down">
                      <span v-if="errors.duration" class="error-message">
                        <i class="fa-solid fa-exclamation-circle"></i>
                        {{ errors.duration }}
                      </span>
                    </transition>
                </div>
                </div>

                <div class="form-group">
                  <label for="venueName">
                    <i class="fa-solid fa-map-marker-alt"></i> {{ config.venueLabel }} *
                  </label>
                  <input
                    id="venueName"
                    v-model="matchForm.venue_name"
                    type="text"
                    :placeholder="config.venuePlaceholder"
                    :class="{ 'error': errors.venue_name }"
                  >
                  <transition name="slide-down">
                    <span v-if="errors.venue_name" class="error-message">
                      <i class="fa-solid fa-exclamation-circle"></i>
                      {{ errors.venue_name }}
                    </span>
                  </transition>
              </div>
              
                <div class="form-group">
                  <label for="matchDescription">
                    <i class="fa-solid fa-align-left"></i> Match Description
                  </label>
                  <textarea
                    id="matchDescription"
                    v-model="matchForm.description"
                    rows="3"
                    placeholder="Any additional information about the match..."
                    maxlength="500"
                  ></textarea>
                  <div class="char-count">{{ matchForm.description.length }}/500</div>
              </div>
              
                <div class="form-actions">
                  <button type="button" class="btn btn-outline" @click="closeTeamModal">
                    Cancel
                </button>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    :disabled="isSubmitting"
                  >
                    <span v-if="!isSubmitting">
                      <i class="fa-solid fa-rocket"></i> Create Match
                    </span>
                    <span v-else>
                      <i class="fa-solid fa-spinner fa-spin"></i> Creating...
                    </span>
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>
      </transition>

      <!-- Success Modal -->
      <transition name="modal">
        <div v-if="showSuccessModal" class="modal-overlay" @click="closeSuccessModal">
          <div class="modal-content success-modal" @click.stop>
          <div class="modal-header">
              <h3><i class="fa-solid fa-circle-check"></i> Match Created!</h3>
              <button class="btn-close" @click="closeSuccessModal" aria-label="Close modal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
              <div class="success-content">
                <div class="success-icon">
                  <i class="fa-solid fa-trophy"></i>
              </div>
                <h4>Match Organized Successfully!</h4>
                <p>Your match against <strong>{{ selectedTeam?.name }}</strong> has been created.</p>
                <div class="success-actions">
                  <button type="button" class="btn btn-primary" @click="closeSuccessModal">
                    <i class="fa-solid fa-eye"></i>
                    View Match Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </transition>
    </div>

    <ThirdpartyFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { kenyaCounties } from "../football/counties.js";
import { getOrganizeMatchConfig } from "./organizeMatchConfig";
import "./organizeMatchSport.css";

export default {
  name: "SportDiscoverTeamsOrganize",

  components: {
    MainNavbar,
    ThirdpartyFooter,
  },

  props: {
    sport: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Search & Filters
      searchQuery: '',
      searchFocused: false,
      showFilters: false,
      filters: {
        county: '',
        constituency: '',
        team_type: '',
        skill_level: '',
        availability: '',
        min_members: null,
        max_members: null,
        verified_only: false
      },
      availableCounties: Object.keys(kenyaCounties),
      availableConstituencies: [],
      sortBy: 'relevance',
      
      // Results
      teams: [],
      loading: false,
      totalTeams: 0,
      currentPage: 1,
      perPage: 20,
      totalPages: 0,
      
      // Team Selection
      selectedTeam: null,
      matchForm: {
        match_date: '',
        match_time: '',
        duration: '',
        venue_name: '',
        description: ''
      },
      errors: {},
      isSubmitting: false,
      showSuccessModal: false,
      
      // Debounce timers
      searchTimer: null,
      filterTimer: null
    };
  },

  computed: {
    config() {
      return getOrganizeMatchConfig(this.sport);
    },

    minDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today.toISOString().split('T')[0];
    },

    activeFiltersCount() {
      let count = 0;
      if (this.filters.county) count++;
      if (this.filters.constituency) count++;
      if (this.filters.team_type) count++;
      if (this.filters.skill_level) count++;
      if (this.filters.availability) count++;
      if (this.filters.min_members !== null && this.filters.min_members !== '') count++;
      if (this.filters.max_members !== null && this.filters.max_members !== '') count++;
      if (this.filters.verified_only) count++;
      for (const extra of this.config.extraFilters || []) {
        if (this.filters[extra.key]) count++;
      }
      return count;
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  beforeMount() {
    sessionStorage.setItem("toafrica_match_sport", this.config.slug);
  },

  watch: {
    sport() {
      sessionStorage.setItem("toafrica_match_sport", this.config.slug);
      this.initSportFilters();
      this.currentPage = 1;
      this.searchTeams();
    },
  },

  mounted() {
    this.initSportFilters();
    if (this.config.discoverEnabled) {
      this.searchTeams();
    } else {
      this.loading = false;
    }
  },

  methods: {
    initSportFilters() {
      for (const extra of this.config.extraFilters || []) {
        if (this.filters[extra.key] === undefined) {
          this.filters[extra.key] = "";
        }
      }
    },

    onSportFilterChange() {
      this.currentPage = 1;
      this.searchTeams();
    },

    async searchTeams() {
      if (!this.config.discoverEnabled) {
        this.teams = [];
        this.totalTeams = 0;
        this.totalPages = 0;
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        const token = localStorage.getItem('access_token') || 
                     localStorage.getItem('token') || 
                     sessionStorage.getItem('access_token') || 
                     sessionStorage.getItem('token');
        
        if (!token) {
            this.$router.push('/ipromiseafrica/sign-in');
          return;
        }

        const params = new URLSearchParams({
          page: this.currentPage.toString(),
          per_page: this.perPage.toString(),
          exclude_my_team: 'true'
        });

        if (this.searchQuery.trim()) {
          params.append('search', this.searchQuery.trim());
        }
        if (this.filters.county) {
          params.append('county', this.filters.county);
        }
        if (this.filters.constituency) {
          params.append('constituency', this.filters.constituency);
        }
        if (this.filters.team_type) {
          params.append('team_type', this.filters.team_type);
        }
        if (this.filters.skill_level) {
          params.append('skill_level', this.filters.skill_level);
        }
        if (this.filters.availability) {
          params.append('availability', this.filters.availability);
        }
        if (this.filters.min_members !== null && this.filters.min_members !== '') {
          params.append('min_members', this.filters.min_members.toString());
        }
        if (this.filters.max_members !== null && this.filters.max_members !== '') {
          params.append('max_members', this.filters.max_members.toString());
        }
        if (this.filters.verified_only) {
          params.append('verified_only', 'true');
        }

        for (const extra of this.config.extraFilters || []) {
          const value = this.filters[extra.key];
          if (value) {
            params.append(extra.key, value);
          }
        }

        const response = await fetch(buildApiUrl(`${this.config.discoverApi}?${params.toString()}`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        this.teams = data.teams || [];
        this.totalTeams = data.total || 0;
        this.totalPages = data.total_pages || 0;
        this.currentPage = data.page || 1;

      } catch (error) {
        console.error('Search error:', error);
        this.showError('Failed to search teams. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    debouncedSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.searchTeams();
      }, 500);
    },

    debouncedFilter() {
      if (this.filterTimer) {
        clearTimeout(this.filterTimer);
      }
      this.filterTimer = setTimeout(() => {
        this.currentPage = 1;
        this.searchTeams();
      }, 500);
    },

    onSearchFocus() {
      this.searchFocused = true;
    },

    onSearchBlur() {
      this.searchFocused = false;
    },

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.searchTeams();
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    onCountyChange() {
      this.filters.constituency = '';
      if (this.filters.county && kenyaCounties[this.filters.county]) {
        this.availableConstituencies = kenyaCounties[this.filters.county];
      } else {
        this.availableConstituencies = [];
      }
      this.currentPage = 1;
      this.searchTeams();
    },

    clearAllFilters() {
      this.searchQuery = '';
      this.filters = {
        county: '',
        constituency: '',
        team_type: '',
        skill_level: '',
        availability: '',
        min_members: null,
        max_members: null,
        verified_only: false,
      };
      this.initSportFilters();
      this.availableConstituencies = [];
      this.currentPage = 1;
      this.searchTeams();
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.searchTeams();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    selectTeam(team) {
      if (!this.config.challengeEnabled || !this.config.challengeRoute) {
        this.showError(
          `Challenges for ${this.config.slug} are coming soon. You can still browse filters and team cards.`,
        );
        return;
      }
      this.$router.push({
        name: this.config.challengeRoute,
        params: { team_id: String(team.team_id) },
        state: { opponentPreview: team },
      });
    },

    closeTeamModal() {
      this.selectedTeam = null;
      this.errors = {};
    },

    async organizeMatch() {
      if (!this.config.createMatchEnabled) {
        this.showError("Match creation for this sport is not available yet.");
        return;
      }

      this.errors = {};

      // Validation
      if (!this.matchForm.match_date) {
        this.errors.match_date = 'Match date is required';
      } else {
        const selectedDate = new Date(this.matchForm.match_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          this.errors.match_date = 'Match date cannot be in the past';
        }
      }

      if (!this.matchForm.match_time) {
        this.errors.match_time = 'Match time is required';
      }

      if (!this.matchForm.duration) {
        this.errors.duration = 'Duration is required';
      }

      if (!this.matchForm.venue_name.trim()) {
        this.errors.venue_name = 'Venue name is required';
      }

      if (Object.keys(this.errors).length > 0) {
        return;
      }

      this.isSubmitting = true;

      try {
        const token = localStorage.getItem('access_token') || 
                     localStorage.getItem('token') || 
                     sessionStorage.getItem('access_token') || 
                     sessionStorage.getItem('token');
        
        if (!token) {
          throw new Error('Authentication required');
        }

        // Get user's team ID
        const teamResponse = await fetch(buildApiUrl(this.config.myTeamApi), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!teamResponse.ok) {
          throw new Error('Failed to fetch your team');
        }

        const teamData = await teamResponse.json();
        const myTeamId = teamData.team_id;

        // Combine date and time
        const matchDateTime = `${this.matchForm.match_date}T${this.matchForm.match_time}:00`;

        const payload = {
          team_id: myTeamId,
          opponent_team_id: this.selectedTeam.team_id,
          opponent_name: this.selectedTeam.name,
          match_date: matchDateTime,
          duration: parseInt(this.matchForm.duration),
          match_type: 'friendly',
          venue: {
            name: this.matchForm.venue_name,
            city: this.selectedTeam.county,
            address: null,
            notes: null
          },
          description: this.matchForm.description || null,
          max_players: 11,
          match_format: '11v11',
          notify_team: true,
          notify_opponent: true
        };

        const createUrl =
          this.config.createMatchApiTemplate?.replace("{teamId}", String(myTeamId)) ||
          `/api/teams/${myTeamId}/matches/create`;
        const response = await fetch(buildApiUrl(createUrl), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          let errorMessage = `Match creation failed (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch (e) {
            errorMessage = response.statusText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        this.closeTeamModal();
        this.showSuccessModal = true;

      } catch (error) {
        console.error('Match creation failed:', error);
        this.showError(error.message || 'Failed to create match. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.$router.push({ path: this.config.dashboardPath }).catch(() => {});
    },

    showError(message) {
      alert(message);
    },

    handleImageError(event) {
      event.target.style.display = 'none';
      const placeholder = event.target.nextElementSibling || event.target.parentElement.querySelector('.logo-placeholder');
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },

    // Helper methods
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    getTeamTypeLabel(type) {
      const types = {
        preschool: 'Pre-School',
        highschool: 'High School',
        academy: 'Academy/Youth',
        casual: 'Casual',
        competitive: 'Competitive',
        corporate: 'Corporate',
        community: 'Community'
      };
      return types[type] || type;
    },

    getSkillLevelLabel(level) {
      const levels = {
        beginner: 'Beginner',
        intermediate: 'Intermediate',
        advanced: 'Advanced',
        elite: 'Elite'
      };
      return levels[level] || level;
    },

    getAvailabilityLabel(availability) {
      const labels = {
        weekends: 'Weekends',
        weekdays: 'Weekdays',
        both: 'Both',
        flexible: 'Flexible'
      };
      return labels[availability] || availability;
    }
  }
};
</script>

<style scoped>
/* ================= PAGE SHELL (full viewport width) ================= */
.organize-match-page {
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: clip;
}

.organize-match-page :deep(.thirdparty-footer) {
  margin-top: 0;
  margin-bottom: 0;
}

.organize-match-page > .discover-teams-page + :deep(.thirdparty-footer) {
  margin-top: 0;
}

/* ================= CSS VARIABLES & BASE ================= */
.discover-teams-page {
  flex: 0 0 auto;
  width: 100%;
  max-width: none;
  margin: 0;
  box-sizing: border-box;
  background: linear-gradient(135deg, var(--background-color2) 0%, var(--background-color1) 100%);
  color: var(--text-color1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  padding: clamp(12px, 2.5vw, 28px);
  padding-top: clamp(72px, 10vw, 100px);
  padding-bottom: 0;
  margin-bottom: 0;
  position: relative;
  z-index: 0;
  overflow-x: clip;
}

.discover-teams-page::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(58, 134, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(0, 151, 0, 0.1) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

/* ================= PAGE HEADER ================= */
.page-header {
  background: var(--background-color1);
  border-radius: clamp(12px, 2vw, 20px);
  padding: clamp(24px, 4vw, 50px) clamp(16px, 4vw, 40px);
  margin-bottom: clamp(16px, 2.5vw, 30px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  overflow: hidden;
  animation: fadeInDown 0.6s ease-out;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.header-content h1 {
  font-size: clamp(28px, 5vw, 40px);
  font-weight: 800;
  color: var(--text-color1);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  position: relative;
  z-index: 1;
}

.header-content h1 i {
  color: var(--border-color-hover);
  font-size: 1.2em;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.header-title-text {
  background: linear-gradient(135deg, var(--text-color1), var(--border-color-hover));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-content p {
  color: var(--text-color2);
  font-size: clamp(14px, 2vw, 18px);
  position: relative;
  z-index: 1;
}

/* ================= SEARCH SECTION ================= */
.search-section {
  background: var(--background-color1);
  border-radius: clamp(12px, 2vw, 20px);
  padding: clamp(16px, 3vw, 28px);
  margin-bottom: clamp(16px, 2.5vw, 30px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: fadeInUp 0.6s ease-out 0.2s both;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.search-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.search-bar-container {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
}

.search-bar {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 20px;
  color: var(--text-color2);
  font-size: 18px;
  z-index: 2;
  transition: all 0.3s ease;
}

.search-input {
  width: 100%;
  padding: 16px 20px 16px 52px;
  border: 2px solid var(--background-color3);
  border-radius: 14px;
  font-size: 16px;
  background: var(--background-color1);
  color: var(--text-color1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-input::placeholder {
  color: var(--text-color2);
  opacity: 0.6;
}

.search-input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 4px rgba(58, 134, 255, 0.1), 0 4px 16px rgba(58, 134, 255, 0.2);
  transform: translateY(-1px);
  animation: inputFocus 0.3s ease;
}

@keyframes inputFocus {
  0% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(58, 134, 255, 0.15), 0 6px 20px rgba(58, 134, 255, 0.25);
  }
  100% {
    box-shadow: 0 0 0 4px rgba(58, 134, 255, 0.1), 0 4px 16px rgba(58, 134, 255, 0.2);
  }
}

.search-input.focused ~ .search-icon {
  color: var(--border-color-hover);
  transform: scale(1.1);
}

.clear-search {
  position: absolute;
  right: 16px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: var(--text-color2);
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  transition: all 0.3s ease;
  z-index: 2;
}

.clear-search:hover {
  background: var(--border-color-hover);
  color: var(--text-color1);
  transform: rotate(90deg) scale(1.1);
}

.btn-filter {
  padding: 16px 28px;
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 14px;
  color: var(--text-color1);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.btn-filter:hover {
  border-color: var(--border-color-hover);
  background: var(--background-color3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-filter.active {
  border-color: var(--border-color-hover);
  background: var(--border-color-hover);
  color: var(--text-color1);
  box-shadow: 0 4px 16px rgba(58, 134, 255, 0.3);
}

.filter-badge {
  background: var(--text-color1);
  color: var(--border-color-hover);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  animation: badgePulse 0.5s ease-out;
}

@keyframes badgePulse {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* ================= FILTERS PANEL ================= */
.filters-panel {
  margin-top: 28px;
  padding-top: 28px;
  border-top: 2px solid var(--background-color3);
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 1000px;
    transform: translateY(0);
  }
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.filters-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-clear-filters {
  padding: 10px 20px;
  background: var(--background-color3);
  border: 2px solid var(--background-color3);
  border-radius: 10px;
  color: var(--text-color2);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-clear-filters:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
  background: transparent;
  transform: translateX(-2px);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: clamp(12px, 2vw, 20px);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label i {
  color: var(--border-color-hover);
  font-size: 16px;
}

.filter-group select,
.filter-group input {
  padding: 12px 16px;
  border: 2px solid var(--background-color3);
  border-radius: 10px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.filter-group select:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
  transform: translateY(-1px);
}

.filter-group input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
  transform: translateY(-1px);
}

.filter-group select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--background-color3);
}

.filter-range {
  grid-column: span 2;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.range-input {
  flex: 1;
}

.range-separator {
  color: var(--text-color2);
  font-weight: 600;
  font-size: 14px;
}

.filter-checkbox {
  grid-column: span 2;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  border-radius: 10px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.checkbox-label:hover {
  background: var(--background-color3);
  border-color: var(--background-color3);
}

.checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--border-color-hover);
  transition: transform 0.2s ease;
}

.checkbox-label input[type="checkbox"]:checked {
  transform: scale(1.1);
}

.checkbox-label span {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

/* ================= RESULTS SECTION ================= */
.results-section {
  background: var(--background-color1);
  border-radius: clamp(12px, 2vw, 20px);
  padding: clamp(16px, 3vw, 32px);
  margin-bottom: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: fadeInUp 0.6s ease-out 0.4s both;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 20px;
}

.results-info h2 {
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 8px;
}

.highlight {
  color: var(--border-color-hover);
  font-weight: 800;
}

.results-subtext {
  color: var(--text-color2);
  font-size: 14px;
}

.sort-options {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sort-options label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 14px;
}

.sort-select {
  padding: 10px 16px;
  border: 2px solid var(--background-color3);
  border-radius: 10px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.sort-select:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
}

/* ================= SKELETON LOADING ================= */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: clamp(16px, 2.5vw, 24px);
}

.skeleton-card {
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 16px;
  padding: 20px;
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

@keyframes skeletonPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.skeleton-logo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--background-color3);
  margin-bottom: 16px;
}

.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 12px;
  background: var(--background-color3);
  border-radius: 6px;
}

.skeleton-title {
  width: 60%;
  height: 18px;
}

.skeleton-subtitle {
  width: 40%;
  height: 14px;
}

.skeleton-text {
  width: 100%;
}

.skeleton-text.short {
  width: 70%;
}

/* ================= LOADING & EMPTY STATES ================= */
.loading-state {
  padding: 60px 20px;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 80px;
  color: var(--text-color2);
  margin-bottom: 24px;
  opacity: 0.4;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.empty-state h3 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 12px;
}

.empty-state p {
  color: var(--text-color2);
  margin-bottom: 32px;
  font-size: 16px;
}

.btn-empty {
  padding: 14px 28px;
  font-size: 16px;
}

/* ================= TEAMS GRID ================= */
.teams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: clamp(16px, 2.5vw, 24px);
  margin-bottom: clamp(24px, 4vw, 40px);
  width: 100%;
}

.team-card {
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 18px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.5s ease-out var(--delay, 0s) both;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}

.team-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--border-color-hover), transparent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}

.team-card:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 40px rgba(58, 134, 255, 0.2);
}

.team-card:hover::before {
  transform: scaleX(1);
}

.team-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.team-logo {
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid var(--background-color3);
  transition: all 0.3s ease;
}

.team-card:hover .team-logo {
  border-color: var(--border-color-hover);
  transform: scale(1.1) rotate(5deg);
}

.team-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.team-card:hover .team-logo img {
  transform: scale(1.1);
}

.logo-placeholder {
  font-size: 32px;
  color: var(--text-color2);
}

.verified-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border: 3px solid var(--background-color1);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  animation: badgeGlow 2s ease-in-out infinite;
}

@keyframes badgeGlow {
  0%, 100% { box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3); }
  50% { box-shadow: 0 2px 16px rgba(76, 175, 80, 0.6); }
}

.team-info h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.team-card:hover .team-info h3 {
  color: var(--border-color-hover);
}

.team-type {
  font-size: 14px;
  color: var(--text-color2);
  font-weight: 500;
}

.team-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.team-bio {
  color: var(--text-color2);
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.team-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid var(--background-color3);
  border-bottom: 1px solid var(--background-color3);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-color2);
  transition: all 0.3s ease;
}

.stat-item:hover {
  color: var(--text-color1);
  transform: translateX(4px);
}

.stat-item i {
  color: var(--border-color-hover);
  font-size: 14px;
}

.team-location {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-color2);
}

.team-location i {
  color: var(--border-color-hover);
}

.team-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.tag-skill {
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.2), rgba(58, 134, 255, 0.1));
  color: var(--border-color-hover);
  border: 1px solid rgba(58, 134, 255, 0.3);
}

.tag-availability {
  background: linear-gradient(135deg, rgba(0, 151, 0, 0.2), rgba(0, 151, 0, 0.1));
  color: #009700;
  border: 1px solid rgba(0, 151, 0, 0.3);
}

.team-card-footer {
  padding-top: 16px;
  border-top: 1px solid var(--background-color3);
}

/* ================= BUTTONS ================= */
.btn {
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 300px;
  height: 300px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--border-color-hover), #4a9eff);
  color: var(--text-color1);
  box-shadow: 0 4px 16px rgba(58, 134, 255, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-primary::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  transform: translate(-50%, -50%);
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1), height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(58, 134, 255, 0.4);
}

.btn-primary:hover:not(:disabled)::after {
  width: 300px;
  height: 300px;
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 134, 255, 0.3);
}

.btn-primary > * {
  position: relative;
  z-index: 1;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-outline {
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  color: var(--text-color1);
}

.btn-outline:hover {
  border-color: var(--border-color-hover);
  background: var(--background-color3);
  transform: translateY(-2px);
}

.btn-sm {
  padding: 10px 20px;
  font-size: 13px;
  width: 100%;
}

.team-card-footer .btn-challenge-team .btn-challenge-team-text {
  color: var(--text-color3);
}

/* ================= PAGINATION ================= */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
  flex-wrap: wrap;
  padding-top: 32px;
  border-top: 2px solid var(--background-color3);
}

.btn-pagination {
  padding: 12px 20px;
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 10px;
  color: var(--text-color1);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-pagination:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  background: var(--background-color3);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-pagination:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-text {
  display: inline-block;
}

.page-numbers {
  display: flex;
  gap: 6px;
}

.btn-page {
  min-width: 44px;
  height: 44px;
  padding: 0 16px;
  background: var(--background-color1);
  border: 2px solid var(--background-color3);
  border-radius: 10px;
  color: var(--text-color1);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-page:hover:not(.active) {
  border-color: var(--border-color-hover);
  background: var(--background-color3);
  transform: translateY(-2px);
}

.btn-page.active {
  background: linear-gradient(135deg, var(--border-color-hover), #4a9eff);
  border-color: var(--border-color-hover);
  color: var(--text-color1);
  box-shadow: 0 4px 12px rgba(58, 134, 255, 0.3);
  transform: scale(1.05);
}

/* ================= MODAL ================= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  backdrop-filter: blur(8px);
}

.modal-content {
  background: var(--background-color1);
  border-radius: 24px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px;
  border-bottom: 2px solid var(--background-color3);
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.1), transparent);
}

.modal-header h3 {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-header h3 i {
  color: var(--border-color-hover);
}

.btn-close {
  background: var(--background-color3);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color2);
  transition: all 0.3s ease;
}

.btn-close:hover {
  background: var(--border-color-hover);
  color: var(--text-color1);
  transform: rotate(90deg) scale(1.1);
}

.modal-body {
  padding: 28px;
}

.selected-team-info {
  text-align: center;
  margin-bottom: 32px;
  padding-bottom: 28px;
  border-bottom: 2px solid var(--background-color3);
}

.team-logo-large {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin: 0 auto 20px;
  overflow: hidden;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--background-color3);
  transition: all 0.3s ease;
  animation: logoFloat 3s ease-in-out infinite;
}

@keyframes logoFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
}

.team-logo-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-placeholder-large {
  font-size: 56px;
  color: var(--text-color2);
}

.selected-team-info h4 {
  font-size: 26px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 12px;
}

.team-details {
  color: var(--text-color2);
  font-size: 15px;
}

.match-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group label i {
  color: var(--border-color-hover);
  font-size: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 14px 16px;
  border: 2px solid var(--background-color3);
  border-radius: 12px;
  background: var(--background-color1);
  color: var(--text-color1);
  font-size: 14px;
  transition: all 0.3s ease;
  font-family: inherit;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 4px rgba(58, 134, 255, 0.1);
  transform: translateY(-1px);
}

.form-group input.error,
.form-group select.error {
  border-color: #f44336;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: var(--text-color2);
  margin-top: 6px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 12px;
}

/* ================= SUCCESS MODAL ================= */
.success-modal .modal-body {
  text-align: center;
  padding: 40px 28px;
}

.success-content {
  padding: 20px;
}

.success-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 42px;
  color: white;
  animation: successPulse 0.6s ease-out;
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.3);
}

@keyframes successPulse {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.success-content h4 {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-color1);
  margin-bottom: 16px;
}

.success-content p {
  color: var(--text-color2);
  margin-bottom: 32px;
  font-size: 16px;
}

.success-actions {
  display: flex;
  justify-content: center;
}

/* ================= TRANSITIONS & ANIMATIONS ================= */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fade transitions */
.fade-enter-active {
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Fade-scale transitions */
.fade-scale-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-scale-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-scale-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(20px);
}

.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Slide-down transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-20px);
}

/* Modal transitions */
.modal-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  opacity: 0;
  transform: scale(0.85) translateY(50px) rotateX(5deg);
}

.modal-enter-to .modal-content,
.modal-leave-from .modal-content {
  opacity: 1;
  transform: scale(1) translateY(0) rotateX(0);
}

/* Team list transitions */
.team-list-enter-active {
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  transition-delay: var(--delay, 0s);
}

.team-list-enter-from {
  opacity: 0;
  transform: translateY(40px) scale(0.9) rotateX(10deg);
}

.team-list-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  width: 100%;
}

.team-list-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(-20px);
}

.team-list-move {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Scale transitions */
.scale-enter-active,
.scale-leave-active {
  transition: all 0.3s ease;
}

.scale-enter-from,
.scale-leave-to {
  opacity: 0;
  transform: scale(0);
}

/* Bounce animation */
.bounce-enter-active {
  animation: bounceIn 0.5s ease-out;
}

@keyframes bounceIn {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* ================= RESPONSIVE DESIGN ================= */
@media (min-width: 1400px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  }
}

@media (min-width: 1800px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  }
}

@media (max-width: 1200px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
  }
  
  .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  }
}

@media (max-width: 1024px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: clamp(14px, 2vw, 20px);
  }
  
  .page-header {
    padding: 36px 28px;
  }
}

@media (max-width: 968px) {
  .discover-teams-page {
    padding: 16px;
    padding-top: 70px;
    padding-bottom: 0;
  }
  
  .page-header {
    padding: 32px 24px;
    animation: fadeInDown 0.5s ease-out;
  }

  .search-section {
    padding: 20px;
    animation: fadeInUp 0.5s ease-out 0.1s both;
  }

  .results-section {
    padding: 24px;
    animation: fadeInUp 0.5s ease-out 0.2s both;
  }

  .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
  }

  .filter-range {
    grid-column: span 1;
  }
  
  .team-card {
    border-radius: 16px;
  }
}

@media (max-width: 768px) {
  .discover-teams-page {
    padding: 12px;
    padding-top: 70px;
    padding-bottom: 0;
  }
  
  .page-header {
    padding: 28px 20px;
    border-radius: 16px;
  }
  
  .header-content h1 {
    flex-direction: column;
    gap: 8px;
  }

  .search-bar-container {
    flex-direction: column;
  }
  
  .btn-filter {
    width: 100%;
    justify-content: center;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filter-range,
  .filter-checkbox {
    grid-column: span 1;
  }

  .range-inputs {
    flex-direction: column;
    gap: 8px;
  }

  .range-separator {
    display: none;
  }

  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .sort-options {
    width: 100%;
  }
  
  .sort-select {
    flex: 1;
    width: 100%;
  }
  
  .teams-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .team-card {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .btn {
    width: 100%;
  }

  .pagination {
    flex-direction: column;
    gap: 16px;
  }
  
  .page-numbers {
    order: -1;
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn-pagination {
    width: 100%;
    justify-content: center;
  }

  .pagination-text {
    display: inline;
  }

  .modal-content {
    max-width: 95%;
    border-radius: 20px;
  }

  .modal-header,
  .modal-body {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .discover-teams-page {
    padding: 8px;
    padding-top: 60px;
    padding-bottom: 0;
  }
  
  .page-header {
    padding: 24px 16px;
    border-radius: 16px;
    margin-bottom: 16px;
  }
  
  .header-content h1 {
    font-size: 24px;
    flex-wrap: wrap;
  }

  .header-content h1 i {
    font-size: 22px;
  }

  .header-content p {
    font-size: 14px;
  }

  .search-section {
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 16px;
  }
  
  .search-bar-container {
    gap: 12px;
  }

  .search-input {
    padding: 14px 16px 14px 44px;
    font-size: 14px;
    border-radius: 12px;
  }
  
  .btn-filter {
    padding: 14px 20px;
    font-size: 14px;
    border-radius: 12px;
  }

  .results-section {
    padding: 16px;
    border-radius: 16px;
  }

  .results-header {
    margin-bottom: 20px;
  }

  .results-info h2 {
    font-size: 20px;
  }

  .team-card {
    padding: 16px;
    border-radius: 14px;
    gap: 14px;
  }

  .team-logo {
    width: 56px;
    height: 56px;
  }

  .team-info h3 {
    font-size: 18px;
  }

  .team-stats {
    gap: 8px;
  }

  .stat-item {
    font-size: 12px;
  }

  .skeleton-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .skeleton-card {
    padding: 16px;
  }

  .btn-sm {
    padding: 10px 16px;
    font-size: 12px;
    width: 100%;
  }

  .pagination {
    gap: 8px;
  }

  .page-numbers {
    gap: 4px;
  }

  .btn-page {
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    font-size: 13px;
  }

  .modal-overlay {
    padding: 10px;
  }
  
  .modal-content {
    max-width: 100%;
    border-radius: 16px;
    margin: 0;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-header h3 {
    font-size: 18px;
  }

  .modal-body {
    padding: 20px;
  }
  
  .team-logo-large {
    width: 80px;
    height: 80px;
  }

  .logo-placeholder-large {
    font-size: 36px;
  }
  
  .selected-team-info {
    margin-bottom: 24px;
    padding-bottom: 20px;
  }

  .selected-team-info h4 {
    font-size: 20px;
  }
  
  .form-group {
    gap: 6px;
  }

  .form-group label {
    font-size: 13px;
  }
  
  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 10px;
    font-size: 14px;
  }

  .empty-state {
    padding: 60px 16px;
  }

  .empty-icon {
    font-size: 64px;
  }

  .empty-state h3 {
    font-size: 22px;
  }
}

@media (max-width: 360px) {
  .discover-teams-page {
    padding: 6px;
    padding-bottom: 0;
  }

  .page-header {
    padding: 20px 12px;
    border-radius: 14px;
  }

  .header-content h1 {
    font-size: 22px;
  }

  .header-content h1 i {
    font-size: 20px;
  }

  .search-section {
    padding: 12px;
  }

  .search-input {
    font-size: 14px;
    padding: 12px 14px 12px 40px;
  }

  .btn-filter {
    padding: 12px 16px;
    font-size: 13px;
  }

  .filters-panel {
    margin-top: 20px;
    padding-top: 20px;
  }

  .filters-grid {
    gap: 12px;
  }

  .results-section {
    padding: 12px;
  }

  .team-card {
    padding: 14px;
    border-radius: 12px;
  }

  .team-logo {
    width: 50px;
    height: 50px;
  }

  .team-info h3 {
    font-size: 16px;
  }

  .btn-page {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    font-size: 12px;
  }

  .btn-pagination {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  .modal-content {
    border-radius: 14px;
  }
  
  .modal-header,
  .modal-body {
    padding: 16px;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .team-card:active {
    transform: scale(0.98);
  }

  .btn:active {
    transform: scale(0.95);
  }

  .btn-filter:active {
    transform: scale(0.95);
  }

  .search-input:focus {
    transform: none;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .team-card,
  .btn,
  .search-input,
  .filter-group select,
  .filter-group input {
    transition: none !important;
  }
  
  .team-card:hover {
    transform: none !important;
  }
}
</style>
